"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.file_generatedBorrowingBaseReport = void 0;
const _ = __importStar(require("lodash"));
const common_1 = require("@finley-technologies/common");
const commonTapeNode_1 = require("../../commonTapeNode");
const utils_1 = require("../../utils");
const types_1 = require("./types");
const { isNode, isIntermediateRecord, ...otherProps } = (0, commonTapeNode_1.commonTapeNodeUtils)(types_1.nodeType, types_1.artifactType, types_1.UIConfigSchema);
// create a new UI node of this type.
const createNewNode = ({ id, suggestedSerialNumber, nodeToCopy, }) => {
    const friendlyName = `Borrowing Base Report`;
    const nodeData = {
        metadata: {
            tapeBuilderNodeId: id,
            tapeBuilderNodeType: types_1.nodeType,
        },
        configuration: {
            artifactSlug: `${_.camelCase(friendlyName)}_${suggestedSerialNumber}`,
            description: 'this is borrowing base report',
            createServicerReport: false,
        },
    };
    if (nodeToCopy) {
        nodeData.configuration = (0, utils_1.copyNodeConfiguration)({
            nodeToCopy,
            isNode,
            nodeType: types_1.nodeType,
            suggestedSerialNumber,
        });
    }
    return nodeData;
};
const isValidS3Key = (key) => {
    const maxLength = 1024;
    if (key.length === 0 || key.length > maxLength) {
        return false;
    }
    // Commonly avoided characters
    // https://docs.aws.amazon.com/AmazonS3/latest/userguide/object-keys.html
    const disallowedCharacters = [
        '^',
        '<',
        '>',
        '{',
        '}',
        '[',
        ']',
        '`',
        '#',
        '%',
        '"',
        '\\',
        '|',
    ];
    for (const char of key) {
        if (disallowedCharacters.includes(char)) {
            return false;
        }
    }
    return true;
};
/*
 * validate the configuration for the node. This checks shape as well
 * logical correctness.
 * This function should validate the config wrt rest of the graph as well.
 * if there is an error it should return an array of error messages.
 * if there are no errors it should return an empty array.
 */
const hasValidConfig = ({ node, friendlyName, inputs, outputs, }) => {
    (0, common_1.finleyAssert)(isNode(node), `node must be a ${types_1.nodeType} node`);
    const allowedInputNodes = [
        'file_generatedReceivableResponseTape',
        'file_generatedCashBalanceFeed',
        'file_generatedCovenantLoanTape',
        'file_intermediateTape',
        'file_assignedRepaymentTape',
        'file_dataLakeTape',
        'file_waitForDataLakeTable',
        'file_generatedSupplementalTape',
    ];
    const errors = (0, utils_1.checkEdges)({
        friendlyName,
        inputs,
        outputs,
        isAllowedInputNode: (uiNode) => allowedInputNodes.some((type) => type === uiNode.nodeType),
        minInputs: 1,
        // Can be many inputs like Receivable Response Tape, Cash Balances, Datalake node, etc.
        maxInputs: 100,
        // note this still requires banks account setup as it pertains to proforma borrowing base reserve and allocation framework
        // even if funding request node is not needed
        minOutputs: 0,
        maxOutputs: 1,
    });
    return errors;
};
const getTapeDefinition = ({ tapeBuilder, artifactId, }) => {
    const tapeDefinitionResult = tapeBuilder.generateTapeDefinition(artifactId);
    (0, common_1.finleyAssert)(tapeDefinitionResult.isValid, `tapeDefinitionResult is not valid for ${artifactId}`);
    (0, common_1.finleyAssert)(tapeDefinitionResult.data, `No definition found for ${artifactId}`);
    return tapeDefinitionResult.data;
};
// validate given IntermediateRecord when funding submission node is available.
const validateIntermediateTape = ({ graph, tape, tapeBuilder, loanProductSlugs, }) => {
    (0, common_1.finleyAssert)(isIntermediateRecord(tape), `validateIntermediateTape: tape is not an ${types_1.artifactType}`);
    (0, common_1.finleyAssert)(tape.type === 'borrowingBaseReport', 'node is not a borrowing base report');
    // if extendedResponseTapeUploads is specified, we want to ensure
    // it refelcts valid loanProductSlugs.
    const errors = [];
    const extendedResponseTapeUploads = tape.configuration.extendedResponseTapeUploads ?? [];
    if (extendedResponseTapeUploads) {
        const allLoanProductSlugs = new Set(loanProductSlugs);
        for (const { loanProductSlug, uploadPath } of extendedResponseTapeUploads) {
            if (!allLoanProductSlugs.has(loanProductSlug)) {
                errors.push(`BorrowingBase Node: [loanProductSlug:${loanProductSlug}] not found in the graph`);
            }
            if (!isValidS3Key(uploadPath)) {
                errors.push(`Invalid upload path [${uploadPath}]`);
            }
        }
    }
    const fundingSubmissionArtifactSlug = tape.configuration.fundingSubmissionArtifactSlug;
    if (fundingSubmissionArtifactSlug) {
        const artifactInputs = (0, utils_1.getDirectInputsForArtifact)({
            targetArtifactId: tape.id,
            ...graph,
        });
        const artifactInput = artifactInputs.find((input) => input.artifactSlug === fundingSubmissionArtifactSlug);
        if (!artifactInput) {
            errors.push(`generatedBorrowingBaseReport: [${tape.artifactSlug}] cannot find [inputArtifact:${fundingSubmissionArtifactSlug}] listed as an input dependency.`);
        }
        else {
            const tapeDefinition = getTapeDefinition({
                tapeBuilder,
                artifactId: artifactInput.id,
            });
            const requiredFields = new Map([
                ['drawRequest#totalAmount', ['float']],
                ['drawRequest#targetFundingDate', ['date']],
                ['collectionsRecycle#totalAmount', ['float']],
                ['collectionsRecycle#targetFundingDate', ['date']],
                ['collectionsPaydown#totalAmount', ['float']],
                ['collectionsPaydown#targetFundingDate', ['date']],
                ['curePaydown#totalAmount', ['float']],
                ['curePaydown#targetFundingDate', ['date']],
            ]);
            const invalidFieldNames = [];
            const invalidFieldTypes = [];
            tapeDefinition.baseFields.forEach((config) => {
                const { field: fieldName, type: fieldType } = config;
                const validTypes = requiredFields.get(fieldName);
                if (validTypes) {
                    if (!validTypes.includes(fieldType)) {
                        invalidFieldTypes.push(`${fieldName} must be one of: [${validTypes.join(',')}]`);
                    }
                    // Remove from field set to return list of missing fields at the end
                    requiredFields.delete(fieldName);
                }
                else {
                    // Add to list of invalid fields
                    invalidFieldNames.push(config.field);
                }
            });
            // Output invalid field types if any
            if (invalidFieldTypes.length > 0) {
                errors.push(`Invalid field types for expected properties: [${invalidFieldTypes.join(', ')}]`);
            }
            // Output invalid field names if any
            if (invalidFieldNames.length > 0) {
                errors.push(`Fields not allowed for funding submission: [${invalidFieldNames.join(', ')}]`);
            }
            // Output missing fields if any
            const missingFields = Array.from(requiredFields.keys());
            if (missingFields.length > 0) {
                errors.push(`Fields missing for funding submission: [${missingFields.join(', ')}]`);
            }
        }
    }
    return errors;
};
/**
 * Represents a UI node with various properties and methods.
 */
exports.file_generatedBorrowingBaseReport = {
    nodeTypeText: 'Borrowing Base',
    nodeType: types_1.nodeType,
    artifactType: types_1.artifactType,
    isOperation: false,
    isRaw: false,
    isFinal: false,
    badgeTexts: ['BorrowingBase'],
    createNewNode,
    isNode,
    isIntermediateRecord,
    ...otherProps,
    hasValidConfig,
    validateIntermediateTape,
};
//# sourceMappingURL=uinode.js.map